import React, {useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef} from 'react'
import {dispatchResolvedRuns} from '../ai-chat/continuationDispatch'
import {ErrorBoundary, type FallbackProps} from 'react-error-boundary'
import {Auth} from 'aws-amplify'
import {
  AssistantRuntimeProvider,
  useExternalStoreRuntime,
  ThreadPrimitive,
  MessagePrimitive,
  useComposerRuntime,
  useMessage,
  type ExternalStoreAdapter,
} from '@assistant-ui/react'
import {BedrockSocketProvider, useBedrockSocket} from '../ai-chat/BedrockSocketProvider'
import {
  readOpenConversationTabs,
  writeOpenConversationTabs,
} from '../ai-chat/openConversationTabs'
import {newAgentSessionId} from '../ai-chat/agentSessionId'
import {resolveShowAiChatRawStreamToggle} from '../ai-chat/resolveShowAiChatRawStreamToggle'
import {
  useChatMessageFormatting,
  generateUniqueId,
  type Message,
} from '../ai-chat/hooks'
import {NO_CONVERSATION_KEY, useFileTags} from '../ai-chat/hooks/useFileTags'
import {useKeyedState} from '../ai-chat/hooks/useKeyedState'
import type {ClearFileTagsFn, ComposerSelectionFn} from '../ai-chat/bedrockSessionController'
import {useCognitoAuth} from '@/app/modules/auth'
import * as chatApi from '../ai-chat/services/chatApi'
import {getLogger, useSubscription} from '@/shared/utils'
import {normalizeAssistantTimeline, timelineEntriesToExportLines} from '../ai-chat/timelineUtils'
import type {OmicsChatSendOptionsRef} from '../ai-chat/hooks/useBedrockStoreAdapter'
import {OMICS_WELCOME_PROMPTS} from '../ai-chat/hooks/useBedrockStoreAdapter'
import {
  useConversationSessions,
  type UseConversationSessionsReturn,
} from '../ai-chat/hooks/useConversationSessions'
import {omicsThreadRuntimeRemountKey} from '../ai-chat/omicsThreadListAdapter'
import {
  isAssistantUiIndexError,
  shouldAutoResetAssistantUiIndexError,
} from '../ai-chat/assistantUiIndexSafety'
import type {AgentWireEvent} from '../ai-chat/agentWireEvent'
import {ConversationTabs} from './ConversationTabs'
import {OmicsComposer, type OmicsComposerProps} from './OmicsComposer'
import {FileTagChip} from './FileTagChip'
import {createOmicsDictationAdapter} from './omicsComposerDictation'
import {AssistantMessageBody, OmicsAssistantRunFooter} from '../ai-chat/components'
import {getOmicsAssistantMessage, getOmicsUserMessage} from '../ai-chat/getOmicsAssistantMessage'
import {subscribeChatUpdated} from '../ai-chat/chatUpdated'
import {
  isUnsetConversationTitle,
  markConversationUpdated,
  resolveConversationTabTitle,
  setConversationTitleInList,
} from '../ai-chat/conversationListHelpers'
import {subscribeJobStatus} from '../ai-chat/jobStatus'
import {
  getPendingRuns,
  hasTrackedRuns,
  hydrateFromServer,
  subscribePendingRunsReconcile,
  removePendingRun,
  subscribePendingRuns,
  getResolvedRuns,
  type PendingRun,
} from '../ai-chat/pendingRuns'
import {coerceAssistantMessageContent} from '../ai-chat/coerceAssistantMessageContent'
import {findInFlightTurn} from '../ai-chat/inFlightTurn'
import {mergePersistedAssistantRunMetadata} from '../ai-chat/mergePersistedAssistantRunMetadata'
import type {ContextUsage} from '../ai-chat/omicsContextUsage'
import {OmicsAssistantSidebarToolUis} from './toolUIs'
import {CustomModal} from '../CustomModal'
import {SaveToCloudDialog} from '../file-picker/SaveToCloudDialog'
import {ExportChatDropdown} from './ExportChatDropdown'
import {ChatFontSizeControl} from './ChatFontSizeControl'
import {CloudContinueControl} from './CloudContinueControl'
import {useChatFontScale} from './chatFontScale'
import {
  useSidePanelResize,
  type SidePanelResizeOptions,
} from '@/ui/layout/panels/useSidePanelResize'
import {PANEL_MIN_WIDTH} from '@/ui/layout/panels/panelWidth'
import './ai-chat-v2.scss'

interface AIChatV2Props {
  width?: number
  onWidthChange?: (width: number) => void
  onClose?: () => void
  /** Width of the Files panel (0 when closed), so resize drag respects the joint budget. */
  otherPanelWidth?: number
  /** When the centre column is hidden, the per-panel handle is unused (shared divider owns the split). */
  centerCollapsed?: boolean
  onCollapseCenter?: SidePanelResizeOptions['onCollapseCenter']
  onPairResize?: SidePanelResizeOptions['onPairResize']
  /** When set, overrides env/ARN-based default for the Raw stream toggle (e.g. tests). */
  showRawStreamToggle?: boolean
}

export interface OmicsAssistantChatLayoutProps extends AIChatV2Props {
  sessions: UseConversationSessionsReturn
  /** Filled by the layout: each conversation's composer attachments + model, read on send. */
  composerSelectionRef: React.MutableRefObject<ComposerSelectionFn>
  /** Filled by the layout: moves the empty-state composer's selection into a new draft tab. */
  draftFromEmptyStateRef: React.MutableRefObject<(conversationId: string) => void>
  clearFileTagsRef: React.MutableRefObject<ClearFileTagsFn | null>
  rawDataMode: boolean
  onRawDataModeChange: (enabled: boolean) => void
  rawWsLog: string
  clearRawWsLog: () => void
  sendOptionsRef: React.MutableRefObject<OmicsChatSendOptionsRef>
  showRawStreamToggle?: boolean
}

interface MyThreadProps {
  /** Omics store: empty + not streaming + not raw mode — do not use ThreadPrimitive.If empty alone (assistant-ui can inject an empty optimistic assistant while isRunning). */
  showWelcomePanel: boolean
  userName?: string
  composerProps: OmicsComposerProps
}

/**
 * How often to refresh the JWT a cloud continuation acts with, while a conversation is opted in.
 * Cognito issues these for 24 h, so an hourly touch during normal use keeps one on hand — without
 * it, a conversation opted in last week has a long-dead token and its continuation stands down.
 */
const CONTINUATION_TOKEN_REFRESH_MS = 60 * 60 * 1000

/**
 * How often to re-ask the server what is still pending, while anything is. Slow on purpose: this is
 * the backstop for a missed notification, not the primary path, and each poll costs a WES status
 * check per unresolved run.
 */
const PENDING_RUNS_POLL_MS = 60_000

/**
 * How often a tab waiting on a turn from before a reload refetches its conversation. Backstop for a
 * missed `chat.updated`, and what ends the wait once the turn is too old to still be running.
 */
const RESUMED_TURN_POLL_MS = 20_000

const WelcomeStarterButtons: React.FC = () => {
  const composer = useComposerRuntime()
  return (
    <div className='aui-welcome__suggestions'>
      {OMICS_WELCOME_PROMPTS.map((prompt) => (
        <button
          key={prompt}
          type='button'
          className='aui-welcome__suggestion'
          onClick={() => {
            composer.setText(prompt)
          }}
        >
          {prompt}
        </button>
      ))}
    </div>
  )
}

const MyThread: React.FC<MyThreadProps> = ({showWelcomePanel, userName, composerProps}) => {
  return (
    <ThreadPrimitive.Root className='aui-root aui-thread-root'>
      <ThreadPrimitive.Viewport className='aui-thread-viewport'>
        {showWelcomePanel ? (
          <div className='aui-welcome'>
            <div className='aui-welcome__icon'>
              <i className='fas fa-dna' />
            </div>
            <h3 className='aui-welcome__title'>
              {userName ? `Hello, ${userName}!` : 'Hello!'}
            </h3>
            <p className='aui-welcome__subtitle'>
              I&apos;m the OmicsAI Agent - your AI assistant for bioinformatics.
              Ask me about gene enrichment, pathway analysis, differential expression,
              or any other bioinformatics topic.
            </p>
            <WelcomeStarterButtons />
          </div>
        ) : null}
        <ThreadPrimitive.Messages
          components={{
            UserMessage: MyUserMessage,
            AssistantMessage: MyAssistantMessage,
          }}
        />
      </ThreadPrimitive.Viewport>
      <OmicsComposer {...composerProps} />
    </ThreadPrimitive.Root>
  )
}

/** Chips that were attached when this prompt was sent (read-only). */
const UserMessageAttachments: React.FC = () => {
  const attachedFiles = useMessage((s) => getOmicsUserMessage(s)?.attachedFiles)
  if (!attachedFiles?.length) return null
  return (
    <div className='file-tags-container aui-message-user__attachments' data-testid='omics-ai-chat-user-attachments'>
      {attachedFiles.map((tag) => (
        <FileTagChip key={tag.id} tag={tag} />
      ))}
    </div>
  )
}

const MyUserMessage: React.FC = () => {
  return (
    <MessagePrimitive.Root className='aui-message aui-message-user'>
      <UserMessageAttachments />
      <MessagePrimitive.Content />
    </MessagePrimitive.Root>
  )
}

/**
 * Clickable follow-up prompt chips generated for this turn (agent `suggested_followups`).
 * Only shown under the latest assistant message (gated by `MessagePrimitive.If last`); each chip
 * sends its prompt as if typed via assistant-ui's `ThreadPrimitive.Suggestion send`.
 */
const SuggestedFollowups: React.FC = () => {
  const followups = useMessage((s) => getOmicsAssistantMessage(s)?.suggestedFollowups)
  if (!followups?.length) return null
  return (
    <div className='aui-followups'>
      {followups.map((fu) => (
        <ThreadPrimitive.Suggestion key={fu} className='aui-followup-chip' prompt={fu} send>
          {fu}
        </ThreadPrimitive.Suggestion>
      ))}
    </div>
  )
}

const MyAssistantMessage: React.FC = () => {
  return (
    <MessagePrimitive.Root className='aui-message aui-message-assistant'>
      <AssistantMessageBody />
      <OmicsAssistantRunFooter />
      <MessagePrimitive.If last>
        <SuggestedFollowups />
      </MessagePrimitive.If>
    </MessagePrimitive.Root>
  )
}

function getLastUserPrompt(messages: readonly Message[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const row = messages[i]
    if (row.type === 'user') {
      const text = row.content?.trim()
      if (text) return row.content
    }
  }
  return null
}

interface OmicsAssistantThreadRuntimeProps {
  adapter: ExternalStoreAdapter<Message>
  showWelcomePanel: boolean
  userName?: string
  rawDataMode: boolean
  rawWsLog: string
  rawWsPreRef?: React.Ref<HTMLPreElement>
  composerProps: OmicsComposerProps
}

const AssistantUiIndexErrorFallback: React.FC<FallbackProps> = ({error, resetErrorBoundary}) => {
  const [showUi, setShowUi] = useState(!isAssistantUiIndexError(error))
  useLayoutEffect(() => {
    if (!isAssistantUiIndexError(error)) return
    if (shouldAutoResetAssistantUiIndexError(error)) {
      resetErrorBoundary()
      return
    }
    setShowUi(true)
  }, [error, resetErrorBoundary])

  if (!showUi) {
    return null
  }

  return (
    <div className='p-3' role='alert'>
      <p className='small text-danger mb-2'>{error.message}</p>
      <button type='button' className='btn btn-sm btn-primary' onClick={resetErrorBoundary}>
        Try again
      </button>
    </div>
  )
}

/** Keyed remount resets assistant-ui MessageRepository after cancel or index shrink. */
const OmicsAssistantThreadRuntime: React.FC<OmicsAssistantThreadRuntimeProps> = ({
  adapter,
  showWelcomePanel,
  userName,
  rawDataMode,
  rawWsLog,
  rawWsPreRef,
  composerProps,
}) => {
  const runtime = useExternalStoreRuntime(adapter)

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <OmicsAssistantSidebarToolUis />
      {rawDataMode ? (
        <div className='ai-chat-v2-raw-layout'>
          <pre ref={rawWsPreRef as React.LegacyRef<HTMLPreElement>} className='ai-chat-v2-raw-ws'>
            {rawWsLog}
          </pre>
          <OmicsComposer {...composerProps} />
        </div>
      ) : (
        <MyThread showWelcomePanel={showWelcomePanel} userName={userName} composerProps={composerProps} />
      )}
    </AssistantRuntimeProvider>
  )
}

export const OmicsAssistantChatLayout: React.FC<OmicsAssistantChatLayoutProps> = ({
  width = 400,
  onWidthChange,
  onClose,
  otherPanelWidth = 0,
  centerCollapsed = false,
  onCollapseCenter,
  onPairResize,
  sessions,
  composerSelectionRef,
  draftFromEmptyStateRef,
  clearFileTagsRef,
  rawDataMode,
  onRawDataModeChange,
  rawWsLog,
  clearRawWsLog,
  showRawStreamToggle = true,
  sendOptionsRef,
}) => {
  const {
    activeConversationId,
    openConversationIds,
    activeAdapter,
    activeThreadContextUsage,
    switchTo,
    ensureDraftSession,
    openConversation,
    closeTab,
    getSession,
    sessionRunningById,
    sessionTitleById,
  } = sessions

  // No cloud units: the chat stays fully browsable, only new turns are blocked. The ref is what
  // every session controller checks in `onNew`; `sendLocked` is the composer's visible state.
  // An expired subscription keeps the chat read-only the same way (`canStartChatTurn` is false).
  const {canStartChatTurn, canUseAiChat, hasSubscription} = useSubscription()
  sendOptionsRef.current.canStartTurn = canStartChatTurn
  const sendLockedTitle = canUseAiChat ? 'No cloud units left' : 'Subscription expired'

  const showWelcomePanel =
    (activeAdapter.messages?.length ?? 0) === 0 &&
    !(activeAdapter.isRunning ?? false) &&
    !rawDataMode

  const lastUserPrompt = useMemo(
    () => getLastUserPrompt(activeAdapter.messages ?? []),
    [activeAdapter.messages],
  )

  // Async job completion. `SocketProvider` marks a run resolved as soon as the `job.status`
  // notification lands, panel open or not; dispatching the follow-up turn needs a live session, so
  // it happens here. Runs whose conversation has no session yet (closed panel, closed tab, an
  // LRU-evicted controller) stay marked and are picked up on the next drain — losing the entry here
  // used to lose the continuation with it.
  //
  // At most one follow-up turn per conversation at a time (see `dispatchResolvedRuns`). Drains are
  // triggered from several places (notification, focus, poll, a turn ending) and each awaits claim
  // round trips, so they are serialized here: a drain asked for while one runs is folded into a
  // rerun once it finishes, instead of both dispatching the same runs.
  const drainInProgressRef = useRef(false)
  const drainRequestedRef = useRef(false)
  const drainResolvedRuns = useCallback(async () => {
    // Leave finished runs marked (and unclaimed) until the user has units again; the callback's
    // identity changes with `canStartChatTurn`, which re-runs the drain on unlock.
    if (!canStartChatTurn) return
    if (drainInProgressRef.current) {
      drainRequestedRef.current = true
      return
    }
    drainInProgressRef.current = true
    try {
      do {
        drainRequestedRef.current = false
        await dispatchResolvedRuns({
          getResolvedRuns,
          getSession: (conversationId) => sessions.getSession(conversationId),
          claim: (runId) => chatApi.claimContinuation(runId),
          release: (runId) => {
            void chatApi.releaseContinuation(runId).catch(() => {
              /* the claim expires on its own; a failed release must not block the turn */
            })
          },
          remove: (runId) => {
            removePendingRun(runId)
          },
          onClaimError: (runId, error) =>
            getLogger().warn(`Continuation claim failed for ${runId}: ${String(error)}`),
        })
      } while (drainRequestedRef.current)
    } finally {
      drainInProgressRef.current = false
    }
  }, [sessions, canStartChatTurn])

  // Runs held back because their conversation was busy go out when its turn ends. Keyed on the set
  // of running conversations, not the map itself, which is rebuilt on every streamed chunk.
  const runningConversationsKey = useMemo(
    () =>
      [...sessionRunningById.entries()]
        .filter(([, running]) => running)
        .map(([id]) => id)
        .sort()
        .join(','),
    [sessionRunningById],
  )
  useEffect(() => {
    void drainResolvedRuns()
  }, [runningConversationsKey, drainResolvedRuns])

  // Re-send the opt-in for the active conversation now and then, which re-captures the caller's
  // JWT server-side (chat-api reads it from the Authorization header). That token is what lets a
  // cloud turn act as the user — read the run's outputs, call WES and MCP — and it lasts 24 h, so
  // it needs topping up while the user is around rather than only at the moment they opt in.
  const lastTokenRefreshRef = useRef(0)
  const refreshContinuationToken = useCallback(async () => {
    const conversationId = activeConversationIdRef.current
    if (!conversationId) return
    if (!conversationsListRef.current.find((c) => c.id === conversationId)?.cloudContinue) return
    if (Date.now() - lastTokenRefreshRef.current < CONTINUATION_TOKEN_REFRESH_MS) return
    lastTokenRefreshRef.current = Date.now()
    try {
      await chatApi.setCloudContinue(conversationId, true)
    } catch (error) {
      // Not worth surfacing: the stored token simply ages out and the run resolves on return.
      getLogger().warn(`Could not refresh the continuation token: ${String(error)}`)
    }
  }, [])

  // Ask the server what is still pending, then act on anything it reports as finished. This is what
  // clears a tag after a notification we never received — a closed laptop, a closed tab, a socket
  // that dropped — because the server re-checks the run's real state as it answers.
  const reconcilePendingRuns = useCallback(async () => {
    try {
      hydrateFromServer(await chatApi.fetchPendingRuns())
    } catch (error) {
      getLogger().warn(`Pending-run reconcile failed: ${String(error)}`)
    }
    void drainResolvedRuns()
    void refreshContinuationToken()
  }, [drainResolvedRuns, refreshContinuationToken])

  // Drain on the live notification (the fast path when a socket is open).
  useEffect(() => {
    void drainResolvedRuns()
    return subscribeJobStatus(() => void drainResolvedRuns())
  }, [drainResolvedRuns])

  // Held in a ref so the effect below can mount once. Depending on the callback directly would tear
  // the interval down and restart it on every render — with renders far more frequent than the poll
  // period, the timer would never actually fire.
  const reconcileRef = useRef(reconcilePendingRuns)
  useEffect(() => {
    reconcileRef.current = reconcilePendingRuns
  }, [reconcilePendingRuns])

  // Reconcile on every way a user can "come back": first paint, the tab regaining focus, and a slow
  // poll while anything is outstanding. The socket's own reconnect is covered by the focus path.
  useEffect(() => {
    void reconcileRef.current()

    const onVisible = () => {
      if (document.visibilityState === 'visible') void reconcileRef.current()
    }
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)

    const timer = window.setInterval(() => {
      if (!hasTrackedRuns()) return
      void reconcileRef.current()
    }, PENDING_RUNS_POLL_MS)

    // A turn that just launched something asks for this, so its tag appears at once rather than up
    // to a poll later — with the id coming from the registry rather than the turn's own prose.
    const unsubscribe = subscribePendingRunsReconcile(() => void reconcileRef.current())

    return () => {
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
      window.clearInterval(timer)
      unsubscribe()
    }
  }, [])

  // Manual escape hatch: a run we can no longer resolve (its notification never arrived, or the
  // agent already reported it in the thread) should not leave a permanent "Running…" tag.
  // Tell the server too — the strip is server-sourced, so a local-only removal is undone by the
  // next poll and the tag reappears within the minute.
  const handleDismissPendingRun = useCallback((runId: string) => {
    const entry = removePendingRun(runId)
    if (entry?.origin === 'server') {
      void chatApi.forgetPendingRun(runId).catch((error) => {
        getLogger().warn(`Dismissing run ${runId} server-side failed: ${String(error)}`)
      })
    }
  }, [])

  // Pending runs for the active conversation (drives the composer "⏳ waiting" strip).
  const [pendingRuns, setPendingRuns] = useState<PendingRun[]>([])
  useEffect(() => {
    const update = () => setPendingRuns(getPendingRuns(activeConversationId))
    update()
    return subscribePendingRuns(update)
  }, [activeConversationId])

  const {currentUser} = useCognitoAuth()

  const {
    setUserS3,
    availableModels,
    selectedModelId,
    conversationsList,
    setConversationsList,
    connectionStatus,
    noteConversationRenamed,
  } = useBedrockSocket()

  // Composer attachments and model are per tab: a new chat starts clean, and switching tabs
  // shows (and sends) only what was picked in that tab.
  const {
    fileTags,
    getFileTags,
    removeFileTag,
    clearFileTags,
    adoptNoConversationTags,
    retainFileTags,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
  } = useFileTags(activeConversationId)

  const modelByConversation = useKeyedState<string>()
  const modelIdFor = (conversationId: string | null): string | null =>
    modelByConversation.get(conversationId ?? NO_CONVERSATION_KEY) ?? selectedModelId
  const selectModelFor = (conversationId: string | null) => (modelId: string) =>
    modelByConversation.update(conversationId ?? NO_CONVERSATION_KEY, () => modelId)

  composerSelectionRef.current = (conversationId) => ({
    fileTags: getFileTags(conversationId),
    chatModelId: modelIdFor(conversationId),
  })

  // Only a turn sent from the empty state hands its selection to the draft it creates; opening any
  // other conversation from there must not inherit it.
  const adoptEmptyComposer = useCallback(
    (conversationId: string) => {
      adoptNoConversationTags(conversationId)
      modelByConversation.move(NO_CONVERSATION_KEY, conversationId)
    },
    [adoptNoConversationTags, modelByConversation],
  )
  draftFromEmptyStateRef.current = adoptEmptyComposer

  // Closed or evicted tabs forget their composer selection.
  useEffect(() => {
    retainFileTags(openConversationIds)
    modelByConversation.retain([NO_CONVERSATION_KEY, ...openConversationIds])
  }, [openConversationIds, retainFileTags, modelByConversation])

  // Wire the user's S3 bucket + Identity Pool prefix into the WebSocket payload
  // so the agent can read/write user files. Without this, the agent receives no
  // S3 context and the LLM will hallucinate a _S3_BUCKET in its <execute> blocks
  // (causing ParamValidationError when boto3 rejects the bogus value).
  // Prefer currentUser.identity_id (Cognito custom:identityId); fall back to
  // Auth.currentCredentials() so Storage and chat use the same prefix.
  useEffect(() => {
    const bucket = process.env.REACT_APP_BUCKET
    const identityId = currentUser?.identity_id
    if (bucket && !identityId && currentUser) {
      Auth.currentCredentials()
        .then((creds) => {
          const id = (creds as {identityId?: string})?.identityId
          if (id) setUserS3({bucket, prefix: id})
          else setUserS3(null)
        })
        .catch(() => setUserS3(null))
      return
    }
    if (bucket && identityId) {
      setUserS3({bucket, prefix: identityId})
    } else {
      setUserS3(null)
    }
  }, [currentUser?.identity_id, currentUser, setUserS3])

  useEffect(() => {
    clearFileTagsRef.current = clearFileTags
  }, [clearFileTags, clearFileTagsRef])

  const {createAgentResponseFromSteps} = useChatMessageFormatting()

  const conversationsListRef = useRef(conversationsList)
  conversationsListRef.current = conversationsList

  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false)
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')

  const historyDropdownRef = useRef<HTMLDivElement>(null)
  const editingInputRef = useRef<HTMLInputElement>(null)
  const rawWsPreRef = useRef<HTMLPreElement>(null)

  const [pendingDelete, setPendingDelete] = useState<{id: string; title: string} | null>(null)

  useEffect(() => {
    if (!rawDataMode || !rawWsLog) return
    const el = rawWsPreRef.current
    if (el) {
      el.scrollTop = el.scrollHeight
    }
  }, [rawDataMode, rawWsLog])

  // Load conversations list on mount
  useEffect(() => {
    chatApi
      .listConversations()
      .then((convs) => setConversationsList(convs))
      .catch((err) => console.error('Failed to load conversations:', err))
  }, [setConversationsList])

  // Restore every tab that was open before the refresh, in its original order, and re-focus the
  // one that was active. Tabs are opened up front so the strip keeps its order however the loads
  // resolve; the retry timers outlive the mount render, so they call through refs.
  const hasRestoredRef = useRef(false)
  const restoringIdsRef = useRef(new Set<string>())
  const loadConversationRef = useRef<typeof loadConversation | null>(null)
  const closeTabRef = useRef(closeTab)
  closeTabRef.current = closeTab
  useEffect(() => {
    if (hasRestoredRef.current) return
    hasRestoredRef.current = true
    const {ids, activeId} = readOpenConversationTabs()
    if (ids.length === 0) {
      ensureDraftSession()
      return
    }
    ids.forEach((id) => {
      restoringIdsRef.current.add(id)
      openConversation(id, undefined, {activate: false})
    })
    if (activeId) switchTo(activeId)

    let failed = 0
    const tryLoad = (id: string, attempt = 0) => {
      const load = loadConversationRef.current
      if (!load) return
      load(id, {activate: false})
        .then(() => restoringIdsRef.current.delete(id))
        .catch(() => {
          if (attempt < 2) {
            setTimeout(() => tryLoad(id, attempt + 1), 1500 * (attempt + 1))
            return
          }
          restoringIdsRef.current.delete(id)
          closeTabRef.current(id)
          failed += 1
          if (failed === ids.length) ensureDraftSession()
        })
    }
    ids.forEach((id) => tryLoad(id))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Remember the open tabs for the next refresh. A draft nobody has typed in yet is left out —
  // there is nothing on the server to reload — while a tab still being restored is kept.
  const lastWrittenTabsRef = useRef('')
  useEffect(() => {
    const isRestorable = (id: string) =>
      restoringIdsRef.current.has(id) ||
      (getSession(id)?.messages.length ?? 0) > 0 ||
      conversationsList.some((c) => c.id === id)
    const ids = [
      ...new Set([...openConversationIds, ...restoringIdsRef.current].filter(isRestorable)),
    ]
    const tabs = {
      ids,
      activeId: activeConversationId && ids.includes(activeConversationId) ? activeConversationId : null,
    }
    const serialized = JSON.stringify(tabs)
    if (serialized === lastWrittenTabsRef.current) return
    lastWrittenTabsRef.current = serialized
    writeOpenConversationTabs(tabs)
    // getSession reads a ref; bumpVersion is what signals a draft got its first message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversationIds, activeConversationId, conversationsList, sessions.bumpVersion])

  const loadConversation = useCallback(
    async (convId: string, options?: {merge?: boolean; activate?: boolean}) => {
      try {
        const data = await chatApi.loadConversationMessages(convId)
        openConversation(data.conversationId, data.sessionId || newAgentSessionId(), {
          activate: options?.activate,
        })
        const controller = sessions.getSession(data.conversationId)
        if (!controller) {
          return
        }
        if (!data.sessionId) {
          controller.agentSessionId = newAgentSessionId()
        }

        const mapped: Message[] = data.messages.map((m) => {
          // The server's messageId is the row's real identity. Minting a fresh one per load made
          // every refetch look like a brand-new set of messages, so a refetch could only ever be a
          // wholesale replace — never a merge into a live thread.
          const id = m.messageId || generateUniqueId()
          const role = (m.role || '').toLowerCase()
          if (role === 'user') {
            return {id, type: 'user' as const, content: m.content}
          }
          if (role === 'assistant') {
            if (m.steps) {
              try {
                const parsed = JSON.parse(m.steps) as unknown
                const timeline = normalizeAssistantTimeline(parsed)
                if (timeline) {
                  const msg = createAgentResponseFromSteps(
                    id,
                    coerceAssistantMessageContent(m.content),
                    [],
                    undefined,
                    undefined,
                    undefined,
                    timeline,
                  )
                  return mergePersistedAssistantRunMetadata(msg, m)
                }
              } catch {
                /* fall through */
              }
            }
            const fallback: Message = {
              id,
              type: 'bot' as const,
              content: m.content
                ? `${coerceAssistantMessageContent(m.content)}\n\n_(This saved message has no v2 timeline; reload after a new agent reply.)_`
                : '_(Empty assistant message)_',
            }
            return mergePersistedAssistantRunMetadata(fallback, m)
          }
          return mergePersistedAssistantRunMetadata(
            {id, type: 'bot' as const, content: coerceAssistantMessageContent(m.content)},
            m,
          )
        })

        // A turn started before a reload is still running server-side but no longer streams here.
        // While the server still shows it unanswered, leave the tab as is (placeholder, Stop).
        const inFlight = findInFlightTurn(data.messages, Date.now(), controller.abandonedRunIds)
        const stillWaiting =
          controller.isAwaitingResumedTurn &&
          inFlight?.runMessageId === controller.resumedRunMessageId
        if (!stillWaiting) {
          // `merge` is for a refetch triggered by someone else (chat.updated): a wholesale replace
          // would kill a turn streaming in this tab. Opening a conversation still replaces, and so
          // does ending a resumed wait — a merge would keep the tab running.
          if (options?.merge && !controller.isAwaitingResumedTurn) {
            controller.mergeThreadMessages(mapped)
          } else {
            controller.replaceThreadMessages(mapped)
          }
          if (inFlight) {
            controller.resumeInFlightTurn(inFlight.runMessageId)
          }
        }
        setConversationsList((prev) => markConversationUpdated(prev, convId, false))
        const listTitle = conversationsListRef.current.find((c) => c.id === convId)?.title?.trim()
        if (listTitle && isUnsetConversationTitle(controller.title)) {
          controller.title = listTitle
        }
        let lastCtx: ContextUsage | null = null
        for (let i = mapped.length - 1; i >= 0; i -= 1) {
          const row = mapped[i]
          if (
            (row.type === 'bot' || row.type === 'agent-response') &&
            row.contextUsage
          ) {
            lastCtx = row.contextUsage
            break
          }
        }
        controller.threadContextUsage = lastCtx
        controller.notifyUpdate()
        if (options?.activate !== false) {
          switchTo(data.conversationId)
        }
      } catch (err) {
        console.error('Failed to load conversation:', err)
        throw err
      }
    },
    [
      createAgentResponseFromSteps,
      openConversation,
      setConversationsList,
      sessions,
      switchTo,
    ]
  )
  loadConversationRef.current = loadConversation

  // Last-conversation restore mounts before history titles are in state, and a new session
  // controller defaults to "Untitled". Copy the persisted name onto any still-placeholder tab.
  useEffect(() => {
    for (const id of openConversationIds) {
      const controller = getSession(id)
      if (!controller || !isUnsetConversationTitle(controller.title)) continue
      const listTitle = conversationsList.find((c) => c.id === id)?.title?.trim()
      if (!listTitle) continue
      controller.title = listTitle
      controller.notifyUpdate()
    }
    // getSession reads a ref; listing it would re-run this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationsList, openConversationIds])

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (historyDropdownRef.current && !historyDropdownRef.current.contains(target)) setShowHistoryDropdown(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Focus rename input when it mounts
  useEffect(() => {
    if (editingConversationId) {
      const t = setTimeout(() => editingInputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [editingConversationId])

  const handleClearChat = useCallback(() => {
    ensureDraftSession()
    setShowHistoryDropdown(false)
    clearRawWsLog()
  }, [ensureDraftSession, clearRawWsLog])

  const dictationAdapter = useMemo(() => createOmicsDictationAdapter(), [])

  const adapterWithExtras = (
    adapter: ExternalStoreAdapter<Message>,
    withDictation: boolean,
  ): ExternalStoreAdapter<Message> => ({
    ...adapter,
    adapters: {
      ...(adapter.adapters ?? {}),
      // Do not attach threadList: swapping threadId inside one runtime is what
      // tapClientLookup hits when jumping tabs. ConversationTabs owns tab switching.
      ...(withDictation && dictationAdapter ? {dictation: dictationAdapter} : {}),
    },
  })

  const tabItems = openConversationIds.map((id) => ({
    id,
    title: resolveConversationTabTitle(
      conversationsList.find((c) => c.id === id)?.title,
      sessionTitleById.get(id),
    ),
    canRename: conversationsList.some((c) => c.id === id && !c.unsaved),
    isRunning: sessionRunningById.get(id) ?? false,
    isActive: id === activeConversationId,
    hasUpdates: conversationsList.find((c) => c.id === id)?.hasUpdates ?? false,
  }))

  // Opt-in to server-side continuation for this conversation. Off by default: a cloud turn is
  // billed like any other, so it happens because the user asked, not because a run happened to
  // finish while they were away.
  const activeCloudContinue = Boolean(
    conversationsList.find((c) => c.id === activeConversationId)?.cloudContinue,
  )
  const changeCloudContinue = useCallback(
    (next: boolean) => {
      if (!activeConversationId || next === activeCloudContinue) return
      // Optimistic: the switch should feel immediate, and a failed PATCH puts it back.
      setConversationsList((prev) =>
        prev.map((c) => (c.id === activeConversationId ? {...c, cloudContinue: next} : c)),
      )
      void chatApi.setCloudContinue(activeConversationId, next).catch((error) => {
        getLogger().warn(`Could not change cloud continuation: ${String(error)}`)
        setConversationsList((prev) =>
          prev.map((c) => (c.id === activeConversationId ? {...c, cloudContinue: !next} : c)),
        )
      })
    },
    [activeCloudContinue, activeConversationId, setConversationsList],
  )

  // A turn landed in one of this user's conversations that this client did not stream — typed on
  // another device, or produced by the agent continuing a finished run on its own. The chat socket
  // only ever answers the connection that asked, so this notification is the only way to find out.
  // Treated as "refetch this", never as data: the payload carries no message content.
  const activeConversationIdRef = useRef(activeConversationId)
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId
  }, [activeConversationId])
  // conversationsListRef is declared above (kept in sync on every render) — reused here rather
  // than shadowed, so the token refresher and the tab-title restore read the same list.

  // Refetch a background tab waiting on a turn from before a reload, so it stops running once the
  // reply lands; the badge then says there is something new, as for any unseen turn.
  const refreshResumedTurn = useCallback(
    (conversationId: string) =>
      loadConversation(conversationId, {merge: true, activate: false}).then(() => {
        if (
          conversationId !== activeConversationIdRef.current &&
          !getSession(conversationId)?.isAwaitingResumedTurn
        ) {
          setConversationsList((prev) => markConversationUpdated(prev, conversationId, true))
        }
      }),
    // getSession reads a ref; listing it would recreate this on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadConversation, setConversationsList],
  )

  useEffect(
    () =>
      subscribeChatUpdated(({conversationId, originMessageId}) => {
        // The echo of a turn still streaming here: the agent persists the reply before the stream
        // ends, and the live copy wins a merge anyway, so the refetch would change nothing.
        if (originMessageId && getSession(conversationId)?.isStreamingRun(originMessageId)) return
        if (conversationId === activeConversationIdRef.current) {
          // Merge, don't replace: a turn may be streaming in this tab right now.
          void loadConversation(conversationId, {merge: true}).catch(() => {
            /* a failed refresh is recoverable — the next open or notification retries */
          })
          return
        }
        if (getSession(conversationId)?.isAwaitingResumedTurn) {
          void refreshResumedTurn(conversationId).catch(() => {
            /* the resumed-turn poll retries */
          })
          return
        }
        setConversationsList((prev) => markConversationUpdated(prev, conversationId, true))
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadConversation, refreshResumedTurn, setConversationsList],
  )

  // Poll while any open tab waits on a turn from before a reload. Keyed on the id list, not the
  // callback, so renders don't keep restarting the timer before it fires.
  const refreshResumedTurnRef = useRef(refreshResumedTurn)
  refreshResumedTurnRef.current = refreshResumedTurn
  const awaitingResumedTurnKey = openConversationIds
    .filter((id) => getSession(id)?.isAwaitingResumedTurn)
    .join(',')
  useEffect(() => {
    if (!awaitingResumedTurnKey) return
    const ids = awaitingResumedTurnKey.split(',')
    const timer = window.setInterval(() => {
      ids.forEach((id) => {
        void refreshResumedTurnRef.current(id).catch(() => {
          /* next tick retries */
        })
      })
    }, RESUMED_TURN_POLL_MS)
    return () => window.clearInterval(timer)
  }, [awaitingResumedTurnKey])

  const handleSelectConversation = useCallback(
    (id: string) => {
      loadConversation(id)
      setShowHistoryDropdown(false)
    },
    [loadConversation]
  )

  const handleStartRename = useCallback(
    (e: React.MouseEvent, c: {id: string; title: string}) => {
      e.stopPropagation()
      setEditingConversationId(c.id)
      setEditingTitle(c.title || 'Untitled')
    },
    []
  )

  // One rename for the history list and the tabs: the list is the tab's title source, so both update.
  // Optimistic, and put back if the PATCH fails.
  const renameConversation = useCallback(
    (id: string, requested: string) => {
      const next = requested.trim()
      const previous = conversationsListRef.current.find((c) => c.id === id)?.title
      if (!next || next === previous) return
      noteConversationRenamed(id)
      setConversationsList((prev) => setConversationTitleInList(prev, id, next))
      chatApi
        .renameConversation(id, next)
        .then(({title}) => setConversationsList((prev) => setConversationTitleInList(prev, id, title)))
        .catch((err) => {
          console.error('Failed to rename:', err)
          if (previous !== undefined) {
            setConversationsList((prev) => setConversationTitleInList(prev, id, previous))
          }
        })
    },
    [noteConversationRenamed, setConversationsList]
  )

  const handleRenameSubmit = useCallback(() => {
    if (editingConversationId) {
      renameConversation(editingConversationId, editingTitle)
    }
    setEditingConversationId(null)
    setEditingTitle('')
  }, [editingConversationId, editingTitle, renameConversation])

  const handleRequestDeleteConversation = useCallback(
    (e: React.MouseEvent, c: {id: string; title: string}) => {
      e.stopPropagation()
      setShowHistoryDropdown(false)
      setPendingDelete({id: c.id, title: c.title?.trim() || 'Untitled'})
    },
    [],
  )

  const handleCancelDeleteConversation = useCallback(() => {
    setPendingDelete(null)
  }, [])

  const handleConfirmDeleteConversation = useCallback(() => {
    if (!pendingDelete) return
    const {id} = pendingDelete
    chatApi
      .deleteConversation(id)
      .then(() => {
        setConversationsList((prev) => prev.filter((conv) => conv.id !== id))
        closeTab(id)
        if (activeConversationId === id) {
          handleClearChat()
        }
        setPendingDelete(null)
      })
      .catch((err) => {
        console.error('Failed to delete:', err)
        setPendingDelete(null)
      })
  }, [activeConversationId, closeTab, pendingDelete, setConversationsList, handleClearChat])

  const [chatToSave, setChatToSave] = useState<{content: string; fileName: string} | null>(null)

  const downloadMarkdown = useCallback((content: string, fileName: string) => {
    const blob = new Blob([content], {type: 'text/markdown;charset=utf-8'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  /** The active chat as Markdown, with the file name both exports use; null when it is empty. */
  const buildChatMarkdown = useCallback((): {content: string; fileName: string} | null => {
    const rows = activeAdapter.messages ?? []
    const visibleMessages = rows.filter(
      (m) => m.type === 'user' || m.type === 'bot' || m.type === 'agent-response',
    )
    if (visibleMessages.length === 0) return null

    const lines: string[] = []
    const conv = conversationsList.find((c) => c.id === activeConversationId)
    const title = conv?.title || 'Chat'
    const date = new Date().toISOString().slice(0, 10)
    lines.push(`# ${title}`)
    lines.push('')
    lines.push(`_Exported on ${date}_`)
    lines.push('')
    lines.push('---')
    lines.push('')

    for (const msg of visibleMessages) {
      if (msg.type === 'user') {
        lines.push('### You')
        lines.push('')
        lines.push(msg.content)
        lines.push('')
      } else {
        lines.push('### Agent')
        lines.push('')
        if (msg.timeline?.schema_version === 2 && msg.timeline.entries.length > 0) {
          lines.push(...timelineEntriesToExportLines(msg.timeline.entries))
        }
        if (msg.content?.trim()) {
          lines.push(msg.content.trim())
          lines.push('')
        }
        if (msg.tokenUsage && msg.tokenUsage.total_tokens > 0) {
          lines.push(
            `> Tokens: ${msg.tokenUsage.input_tokens} in / ${msg.tokenUsage.output_tokens} out (${msg.tokenUsage.llm_calls} calls)`,
          )
          lines.push('')
        }
      }
    }

    const safeTitle = title.replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 60)
    return {content: lines.join('\n'), fileName: `${safeTitle}_${date}.md`}
  }, [activeAdapter.messages, activeConversationId, conversationsList])

  const downloadChat = useCallback(() => {
    const chat = buildChatMarkdown()
    if (chat) downloadMarkdown(chat.content, chat.fileName)
  }, [buildChatMarkdown, downloadMarkdown])

  const saveChatToCloud = useCallback(() => setChatToSave(buildChatMarkdown()), [buildChatMarkdown])

  // Shared with FilesPanel: joint clamp + iframe-safe drag shield.
  const getOtherPanelWidth = useCallback(() => otherPanelWidth, [otherPanelWidth])
  const getCenterCollapsed = useCallback(() => centerCollapsed, [centerCollapsed])
  const handleResizePointerDown = useSidePanelResize('right', onWidthChange, getOtherPanelWidth, {
    getCenterCollapsed,
    onCollapseCenter,
    onPairResize,
  })

  const {scale: chatFontScale, setScale: setChatFontScale} = useChatFontScale()

  return (
    <>
      <div
        className='ai-chat-v2-sidebar'
        data-testid='omics-ai-chat-sidebar'
        data-connection-status={connectionStatus}
        style={{
          width: `${width}px`,
          minWidth: `${PANEL_MIN_WIDTH}px`,
          maxWidth: centerCollapsed ? undefined : '80vw',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          borderLeft: centerCollapsed
            ? 'none'
            : '1px solid var(--bs-border-color, #dee2e6)',
          background: 'var(--bs-body-bg, #fff)',
          position: 'relative',
          // Read by the conversation's font-size tokens in ai-chat-v2.scss.
          ['--ob-chat-font-scale' as string]: chatFontScale,
        }}
      >
        {!centerCollapsed && (
          <div
            className='ai-chat-v2-sidebar__resize-handle'
            onMouseDown={handleResizePointerDown}
            title='Drag to resize'
          />
        )}

        {/* Header */}
        <div className='ai-chat-v2-sidebar__header'>
          <div className='ai-chat-v2-sidebar__header-left'>
            <div className='ai-chat-v2-sidebar__title-block'>
              <span className='ai-chat-v2-sidebar__title'>
                OmicsAI Agent
              </span>
            </div>
            {showRawStreamToggle ? (
              <button
                type='button'
                className={`ai-chat-v2-sidebar__raw-toggle ${rawDataMode ? 'active' : ''}`}
                title='Show each WebSocket frame exactly as received (no chat formatting)'
                aria-pressed={rawDataMode}
                onClick={() => onRawDataModeChange(!rawDataMode)}
              >
                Raw stream
              </button>
            ) : null}
          </div>

          <div className='ai-chat-v2-sidebar__header-actions'>
            <button
              className='ai-chat-v2-sidebar__icon-btn'
              data-testid='omics-ai-chat-new-btn'
              onClick={handleClearChat}
              title='New chat'
              aria-label='New chat'
            >
              <i className='fas fa-plus' />
            </button>

            <CloudContinueControl
              enabled={activeCloudContinue}
              onChange={changeCloudContinue}
              disabled={!activeConversationId}
            />

            <ExportChatDropdown
              onDownload={downloadChat}
              onSaveToCloud={saveChatToCloud}
              saveDisabledReason={
                hasSubscription ? undefined : 'Saving to the cloud needs a subscription with My Files.'
              }
            />

            <ChatFontSizeControl scale={chatFontScale} onChange={setChatFontScale} />

            <div className='ai-chat-v2-sidebar__dropdown' ref={historyDropdownRef}>
              <button
                type='button'
                data-testid='omics-ai-chat-history-btn'
                className={`ai-chat-v2-sidebar__icon-btn ${showHistoryDropdown ? 'active' : ''}`}
                onClick={() => setShowHistoryDropdown((v) => !v)}
                title='Chat history'
              >
                <i className='fas fa-clock-rotate-left' />
              </button>
              {showHistoryDropdown && (
                <div className='ai-chat-v2-sidebar__dropdown-menu ai-chat-v2-sidebar__dropdown-menu--history'>
                  {conversationsList.length === 0 ? (
                    <div className='ai-chat-v2-sidebar__dropdown-empty'>
                      No past conversations
                    </div>
                  ) : (
                    conversationsList.map((c) => (
                      <div
                        key={c.id}
                        className={`ai-chat-v2-sidebar__history-item ${activeConversationId === c.id ? 'active' : ''}`}
                        role='button'
                        tabIndex={0}
                        onClick={() => !editingConversationId && handleSelectConversation(c.id)}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && !editingConversationId) {
                            e.preventDefault()
                            handleSelectConversation(c.id)
                          }
                        }}
                      >
                        <div className='ai-chat-v2-sidebar__history-content'>
                          {editingConversationId === c.id ? (
                            <input
                              ref={editingInputRef}
                              type='text'
                              className='ai-chat-v2-sidebar__history-edit-input'
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onBlur={handleRenameSubmit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  handleRenameSubmit()
                                }
                                if (e.key === 'Escape') {
                                  setEditingConversationId(null)
                                  setEditingTitle('')
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <>
                              <span className='ai-chat-v2-sidebar__history-title' title={c.title}>
                                {c.title || 'Untitled'}
                              </span>
                              <span className='ai-chat-v2-sidebar__history-date'>
                                {new Date(c.updatedAt).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                        {editingConversationId !== c.id && (
                          <div
                            className='ai-chat-v2-sidebar__history-actions'
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className='ai-chat-v2-sidebar__history-action'
                              onClick={(e) => handleStartRename(e, c)}
                              disabled={c.unsaved}
                              title={c.unsaved ? 'Available once the first reply starts' : 'Rename'}
                              aria-label='Rename conversation'
                            >
                              <i className='fas fa-pencil' />
                            </button>
                            <button
                              className='ai-chat-v2-sidebar__history-action ai-chat-v2-sidebar__history-action--danger'
                              type='button'
                              data-testid='omics-ai-chat-history-delete-btn'
                              onClick={(e) => handleRequestDeleteConversation(e, c)}
                              title='Delete'
                            >
                              <i className='fas fa-trash' />
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {onClose && (
              <button
                className='ai-chat-v2-sidebar__icon-btn d-lg-none'
                onClick={onClose}
                title='Close chat'
              >
                <i className='fas fa-xmark' />
              </button>
            )}
          </div>
        </div>

        <ConversationTabs
          tabs={tabItems}
          onSelect={(id) => {
            switchTo(id)
            const session = sessions.getSession(id)
            if (session && !session.hydrated && conversationsList.some((c) => c.id === id)) {
              void loadConversation(id)
            }
          }}
          onClose={(id) => {
            if (!closeTab(id)) {
              window.alert('Stop the running agent before closing this tab.')
            }
          }}
          onRename={renameConversation}
        />

        {/* Body */}
        <div className='ai-chat-v2-sidebar__body'>
          {openConversationIds.length === 0 ? (
            <ErrorBoundary
              resetKeys={[omicsThreadRuntimeRemountKey(null, 0)]}
              fallbackRender={(p) => <AssistantUiIndexErrorFallback {...p} />}
            >
              <OmicsAssistantThreadRuntime
                key={omicsThreadRuntimeRemountKey(null, 0)}
                adapter={adapterWithExtras(activeAdapter, true)}
                showWelcomePanel={showWelcomePanel}
                userName={currentUser?.name}
                rawDataMode={rawDataMode}
                rawWsLog={rawWsLog}
                rawWsPreRef={rawWsPreRef}
                composerProps={{
                  fileTags,
                  onRemoveTag: removeFileTag,
                  handleDragOver,
                  handleDragEnter,
                  handleDragLeave,
                  handleDrop,
                  isRunning: activeAdapter.isRunning ?? false,
                  threadContextUsage: activeThreadContextUsage,
                  lastUserPrompt,
                  pendingRuns,
                  onDismissPendingRun: handleDismissPendingRun,
                  availableModels,
                  selectedModelId: modelIdFor(null),
                  onSelectModel: selectModelFor(null),
                  dictationEnabled: Boolean(dictationAdapter),
                  sendLocked: !canStartChatTurn,
                  sendLockedTitle,
                }}
              />
            </ErrorBoundary>
          ) : (
            openConversationIds.map((id) => {
              const session = getSession(id)
              if (!session) return null
              const isActive = id === activeConversationId
              const sessionAdapter = session.getAdapter()
              const resyncKey = session.threadResyncKey
              return (
                <div
                  key={id}
                  className='ai-chat-v2-sidebar__runtime-slot'
                  hidden={!isActive}
                  aria-hidden={!isActive}
                >
                  <ErrorBoundary
                    resetKeys={[omicsThreadRuntimeRemountKey(id, resyncKey)]}
                    fallbackRender={(p) => <AssistantUiIndexErrorFallback {...p} />}
                  >
                    <OmicsAssistantThreadRuntime
                      key={omicsThreadRuntimeRemountKey(id, resyncKey)}
                      adapter={adapterWithExtras(sessionAdapter, isActive)}
                      showWelcomePanel={
                        (sessionAdapter.messages?.length ?? 0) === 0 &&
                        !(sessionAdapter.isRunning ?? false) &&
                        !rawDataMode
                      }
                      userName={currentUser?.name}
                      rawDataMode={isActive && rawDataMode}
                      rawWsLog={rawWsLog}
                      rawWsPreRef={isActive ? rawWsPreRef : undefined}
                      composerProps={{
                        fileTags: isActive ? fileTags : getFileTags(id),
                        onRemoveTag: removeFileTag,
                        handleDragOver,
                        handleDragEnter,
                        handleDragLeave,
                        handleDrop,
                        isRunning: sessionAdapter.isRunning ?? false,
                        threadContextUsage: session.threadContextUsage,
                        lastUserPrompt: getLastUserPrompt(sessionAdapter.messages ?? []),
                        pendingRuns: isActive ? pendingRuns : [],
                        onDismissPendingRun: handleDismissPendingRun,
                        availableModels,
                        selectedModelId: modelIdFor(id),
                        onSelectModel: selectModelFor(id),
                        dictationEnabled: isActive && Boolean(dictationAdapter),
                        sendLocked: !canStartChatTurn,
                        sendLockedTitle,
                      }}
                    />
                  </ErrorBoundary>
                </div>
              )
            })
          )}
        </div>
      </div>
      {pendingDelete ? (
        <CustomModal
          title='Delete conversation?'
          closeModal={handleCancelDeleteConversation}
          buttons={[
            {text: 'Cancel', className: 'btn btn-light', onClick: handleCancelDeleteConversation},
            {
              text: 'Delete',
              className: 'btn btn-danger',
              onClick: handleConfirmDeleteConversation,
              type: 'button',
              dataTestId: 'omics-ai-chat-delete-confirm-btn',
            },
          ]}
        >
          <p className='mb-0'>
            Are you sure you want to irrevocably delete the conversation titled{' '}
            <strong>{pendingDelete.title}</strong>?
          </p>
        </CustomModal>
      ) : null}
      {chatToSave ? (
        <SaveToCloudDialog
          fileName={chatToSave.fileName}
          content={chatToSave.content}
          contentType='text/markdown;charset=utf-8'
          onClose={() => setChatToSave(null)}
        />
      ) : null}
    </>
  )
}

const OmicsChatSessionShell: React.FC<
  AIChatV2Props & {
    wireHandlerRef: React.MutableRefObject<(event: AgentWireEvent) => void>
    rawWebSocketFrameSinkRef: React.MutableRefObject<((t: string) => void) | null>
    showRawStreamToggle?: boolean
  }
> = ({
  wireHandlerRef,
  rawWebSocketFrameSinkRef,
  showRawStreamToggle: showRawStreamToggleProp,
  ...layoutProps
}) => {
  const rawDataModeRef = useRef(false)
  const [rawDataMode, setRawDataMode] = useState(false)
  rawDataModeRef.current = rawDataMode

  const [rawWsLog, setRawWsLog] = useState('')
  const clearRawWsLog = useCallback(() => setRawWsLog(''), [])
  const appendRawWs = useCallback((t: string) => {
    setRawWsLog((prev) => (prev === '' ? t : `${prev}\n${t}`))
  }, [])
  useEffect(() => {
    rawWebSocketFrameSinkRef.current = rawDataMode ? appendRawWs : null
  }, [rawDataMode, appendRawWs, rawWebSocketFrameSinkRef])
  const sendOptionsRef = useRef<OmicsChatSendOptionsRef>({mode: 'research'})
  const composerSelectionRef = useRef<ComposerSelectionFn>(() => ({fileTags: [], chatModelId: null}))
  const draftFromEmptyStateRef = useRef<(conversationId: string) => void>(() => {})
  const handleDraftFromEmptyState = useCallback(
    (conversationId: string) => draftFromEmptyStateRef.current(conversationId),
    [],
  )
  const clearFileTagsRef = useRef<ClearFileTagsFn | null>(null)

  const {sendForSession, cancelForSession, touchConversationListEntry} = useBedrockSocket()

  const sessions = useConversationSessions({
    rawStreamModeRef: rawDataModeRef,
    sendOptionsRef,
    composerSelectionRef,
    clearFileTagsRef,
    onDraftFromEmptyState: handleDraftFromEmptyState,
    sendForSession,
    cancelForSession,
    onConversationListTouch: touchConversationListEntry,
  })

  useEffect(() => {
    wireHandlerRef.current = sessions.dispatchWireEvent
  }, [sessions.dispatchWireEvent, wireHandlerRef])

  const handleRawDataModeChange = useCallback((enabled: boolean) => {
    setRawDataMode(enabled)
    setRawWsLog('')
  }, [])

  return (
    <OmicsAssistantChatLayout
      {...layoutProps}
      showRawStreamToggle={showRawStreamToggleProp ?? resolveShowAiChatRawStreamToggle()}
      sessions={sessions}
      composerSelectionRef={composerSelectionRef}
      draftFromEmptyStateRef={draftFromEmptyStateRef}
      clearFileTagsRef={clearFileTagsRef}
      sendOptionsRef={sendOptionsRef}
      rawDataMode={rawDataMode}
      onRawDataModeChange={handleRawDataModeChange}
      rawWsLog={rawWsLog}
      clearRawWsLog={clearRawWsLog}
    />
  )
}

export const AIChatV2Wrapper: React.FC<AIChatV2Props> = (props) => {
  const wireHandlerRef = useRef<(event: AgentWireEvent) => void>(() => {})
  const rawWsSinkRef = useRef<((t: string) => void) | null>(null)

  return (
    <BedrockSocketProvider
      onWireEvent={(event) => wireHandlerRef.current(event)}
      onTransportError={(error) => {
        wireHandlerRef.current({type: 'error', error})
      }}
      rawWebSocketFrameSinkRef={rawWsSinkRef}
      autoReconnect
    >
      <OmicsChatSessionShell
        {...props}
        wireHandlerRef={wireHandlerRef}
        rawWebSocketFrameSinkRef={rawWsSinkRef}
      />
    </BedrockSocketProvider>
  )
}
